<template>
  <div class="container mt-4">

    <!-- Selectores para estado y lote -->
    <div class="row mb-4">
      <div class="col-md-6">
        <label for="estado" class="form-label">Estado de Plantas del Lote:</label>
        <select id="estado" v-model="estadoSeleccionado" class="form-select" @change="cargarLotes">
          <option value="" disabled>Selecciona un estado</option>
          <option v-for="estado in estados" :value="estado" :key="estado">
            {{ estado }}
          </option>
        </select>
      </div>
      <div class="col-md-6">
        <label for="lote" class="form-label">Selecciona un Lote:</label>
        <select id="lote" v-model="loteSeleccionado" class="form-select" @change="cargarPlantas"
          :disabled="!estadoSeleccionado">
          <option value="" disabled>Selecciona un lote</option>
          <option v-for="lote in lotes" :value="lote.id" :key="lote.id">
            {{ lote.nombre }} ({{ lote.fecha_inicio }})
          </option>
        </select>
      </div>
    </div>

    <!-- Mensaje en caso de no haber lotes -->
    <div v-if="lotes.length === 0 && estadoSeleccionado" class="alert alert-info">
      No hay lotes disponibles para el estado seleccionado. Recuerde mover las Plantas o el Lote al evento de Cultivo actual en el que quiere cosechar la Biomasa.
    </div>

    <!-- Card de selección de plantas -->
    <div v-if="plantas.length > 0" class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5>Plantas del Lote: {{ loteSeleccionadoNombre }}</h5>
        <div>
          <!-- Botón para colapsar/expandir con icono y texto descriptivo -->
          <button type="button" class="btn btn-outline-secondary me-2" @click="toggleCollapse">
            <font-awesome-icon :icon="collapsed ? 'toggle-off' : 'toggle-on'" />
            <span class="ms-1">{{ collapsed ? 'Expandir' : 'Colapsar' }}</span>
          </button>
          <!-- Botón para seleccionar/deseleccionar todas con icono y texto descriptivo -->
          <button type="button" class="btn btn-outline-primary" @click="seleccionarTodas">
            <font-awesome-icon :icon="todasSeleccionadas ? 'check-square' : ['far', 'check-square']" />
            <span class="ms-1">{{ todasSeleccionadas ? 'Deseleccionar Todas' : 'Seleccionar Todas' }}</span>
          </button>
          <!-- Botón para invertir selección -->
          <button type="button" class="btn btn-outline-warning ms-2" @click="invertirSeleccion">
            <font-awesome-icon icon="random" />
            <span class="ms-1">Invertir Selección</span>
          </button>

        </div>
      </div>




      <!-- Contenido expandido: listado, buscador y paginación -->
      <transition name="fade">
        <div v-if="!collapsed" class="card-body">
          <div class="mb-3">
            <input type="text" class="form-control" placeholder="Buscar por nombre de planta" v-model="busquedaNombre"
              @input="filtrarPlantas" />
          </div>
          <!-- Plantas como tarjetas -->
          <div class="row g-2 plantas-grid">
            <div v-for="planta in plantasPaginadas" :key="planta.id" class="col-12 col-sm-6 col-md-4 col-lg-3">
              <div class="card h-100 p-2 d-flex flex-column justify-content-between shadow-sm">
                <div>
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" :id="'planta-' + planta.id" :value="planta"
                      v-model="plantasSeleccionadas" @change="emitirSeleccion" />
                    <label class="form-check-label" :for="'planta-' + planta.id">
                      {{ planta.codigo }}
                    </label>
                  </div>
                  <p class="mb-1"><small><strong>Variedad:</strong> {{ planta.variedad.nombre }}</small></p>
                </div>
              </div>
            </div>
          </div>

          <nav aria-label="Paginación de Plantas" class="mt-3">
            <ul class="pagination justify-content-center">
              <li class="page-item" :class="{ disabled: paginaActual === 1 }">
                <button class="page-link" @click="cambiarPagina(paginaActual - 1)">
                  <i class="bi bi-chevron-left"></i>
                </button>
              </li>
              <li class="page-item" v-for="pagina in totalPaginas" :key="pagina"
                :class="{ active: pagina === paginaActual }">
                <button class="page-link" @click="cambiarPagina(pagina)">{{ pagina }}</button>
              </li>
              <li class="page-item" :class="{ disabled: paginaActual === totalPaginas }">
                <button class="page-link" @click="cambiarPagina(paginaActual + 1)">
                  <i class="bi bi-chevron-right"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </transition>
      <!-- Contenido en modo colapsado: resumen de selección -->
      <transition name="fade">
        <div v-if="collapsed" class="card-body">
          <p class="mb-0">
            <strong>Resumen de selección:</strong>
            <span v-if="plantasSeleccionadas.length === 0">No hay plantas seleccionadas.</span>
            <span v-else-if="plantasSeleccionadas.length <= 3">{{ resumenSeleccionadas }}</span>
            <span v-else>{{ plantasSeleccionadas.length }} plantas seleccionadas</span>
          </p>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import axios from '@/axios';

export default {
  data() {
    return {
      lotes: [],
      loteSeleccionado: '',
      plantas: [],
      plantasFiltradas: [],
      plantasSeleccionadas: [],
      estados: [],
      estadoSeleccionado: '',
      paginaActual: 1,
      plantasPorPagina: 10,
      busquedaNombre: '',
      clubActual: this.$store.getters.getClubActual,
      collapsed: false, // Estado para controlar la colapsabilidad
    };
  },
  props: {
    estadosFiltrados: {
      type: Array,
      default: () => [],
    },
    proyecto_id: {
      type: Number,
      required: true,
    },
    proyecto_evento_id: {
      type: Number,
    },
  },
  computed: {
    loteSeleccionadoNombre() {
      const lote = this.lotes.find((l) => l.id === this.loteSeleccionado);
      return lote ? lote.nombre : '';
    },
    totalPaginas() {
      return Math.ceil(this.plantasFiltradas.length / this.plantasPorPagina);
    },
    plantasPaginadas() {
      const inicio = (this.paginaActual - 1) * this.plantasPorPagina;
      return this.plantasFiltradas.slice(inicio, inicio + this.plantasPorPagina);
    },
    todasSeleccionadas() {
      return this.plantasSeleccionadas.length === this.plantasFiltradas.length && this.plantasFiltradas.length > 0;
    },
    resumenSeleccionadas() {
      if (this.plantasSeleccionadas.length === 0) {
        return '';
      }
      if (this.plantasSeleccionadas.length <= 3) {
        return this.plantasSeleccionadas.map(p => p.codigo).join(', ');
      }
      return `${this.plantasSeleccionadas.length} plantas seleccionadas`;
    },
  },
  methods: {
    async cargarEstados() {
      try {
        const response = await axios.get(`/api/constantes/estados_de_una_planta`);
        let estados = response.data;

        if (this.estadosFiltrados.length > 0) {
          estados = estados.filter(estado => this.estadosFiltrados.includes(estado));
        }

        this.estados = estados;
        return Promise.resolve();
      } catch (error) {
        console.error('Error al cargar los estados:', error);
        return Promise.reject();
      }
    },
    async cargarLotes() {
      if (!this.estadoSeleccionado) return;
      try {


        const response = await axios.get(`/api/lotes/bloqueados/${this.estadoSeleccionado}`, {
          params: { club_id: this.clubActual.id, proyecto_id: this.proyecto_id, proyecto_evento_id: this.proyecto_evento_id },
        });
        this.lotes = response.data.lotes;



        this.loteSeleccionado = '';
        this.plantas = [];
        this.plantasFiltradas = [];
        this.busquedaNombre = '';
        this.lotes.forEach((lote) => {
          lote.fecha_inicio = new Date(lote.fecha_inicio).toLocaleDateString();
        });

        if (this.lotes.length > 0) {
          this.loteSeleccionado = this.lotes[0].id;
          this.cargarPlantas();
        }

      } catch (error) {
        console.error('Error al cargar los lotes:', error);
      }
    },
    async cargarPlantas() {
      if (!this.loteSeleccionado) return;
      try {
        const lote = this.lotes.find((l) => l.id === this.loteSeleccionado);
        this.plantas = lote ? lote.plantas : [];
        // Filtrar las plantas por estado seleccionado
        this.plantas = this.plantas.filter((planta) => planta.estado === this.estadoSeleccionado);

        this.plantasFiltradas = [...this.plantas];
        this.paginaActual = 1;
      } catch (error) {
        console.error('Error al cargar las plantas:', error);
      }
    },
    filtrarPlantas() {
      const termino = this.busquedaNombre.toLowerCase();
      this.plantasFiltradas = this.plantas.filter((planta) =>
        planta.codigo.toLowerCase().includes(termino)
      );
      this.paginaActual = 1;
    },
    seleccionarTodas() {
      if (this.todasSeleccionadas) {
        this.plantasSeleccionadas = [];
      } else {
        this.plantasSeleccionadas = [...this.plantasFiltradas];
      }
      this.emitirSeleccion();
    },
    emitirSeleccion() {
      this.$emit('plantas-seleccionadas', [...this.plantasSeleccionadas]);
    },
    cambiarPagina(nuevaPagina) {
      if (nuevaPagina > 0 && nuevaPagina <= this.totalPaginas) {
        this.paginaActual = nuevaPagina;
      }
    },
    toggleCollapse() {
      this.collapsed = !this.collapsed;
    },
    invertirSeleccion() {
      const nuevasSeleccionadas = this.plantasFiltradas.filter(planta =>
        !this.plantasSeleccionadas.includes(planta)
      );
      this.plantasSeleccionadas = nuevasSeleccionadas;
      this.emitirSeleccion();
    }

  },
  mounted() {
    this.cargarEstados().then(() => {
      if (!this.estadoSeleccionado && this.estados.length > 0) {
        this.estadoSeleccionado = this.estados[0];
        this.cargarLotes(); // Cargar lotes para ese estado por defecto
      }
    });
  }

};
</script>

<style scoped>
.container {
  max-width: 900px;
}

.card {
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.table-responsive {
  max-height: 300px;
  overflow-y: auto;
}

.pagination {
  margin-top: 20px;
}

.page-item.disabled .page-link {
  cursor: not-allowed;
}

.page-item.active .page-link {
  background-color: #28a745;
  border-color: #28a745;
  color: white;
}

.btn-primary {
  background-color: #28a745;
  border-color: #28a745;
}

.btn-primary:hover {
  background-color: #218838;
  border-color: #1e7e34;
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn-secondary:hover {
  background-color: #5a6268;
  border-color: #545b62;
}

.bi {
  font-size: 1.2rem;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.plantas-grid {
  margin-top: 10px;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 5px;
}

.plantas-grid::-webkit-scrollbar {
  width: 5px;
}

.plantas-grid::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}

.card {
  transition: transform 0.2s ease-in-out;
}

.card:hover {
  transform: scale(1.01);
}
</style>
